/**
 * Supplier API service using BaseApi pattern
 * Reduced from 160 lines to ~110 lines (31% reduction)
 * All features preserved: suppliers CRUD, purchase orders, performance, analytics
 */

import type {
  CreatePurchaseOrder,
  CreateSupplier,
  CreateSupplierPaymentTerm,
  CreateSupplierProduct,
  ExportOptions,
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  PurchaseOrderWithRelations,
  Supplier,
  SupplierPaymentTerm,
  SupplierProductWithRelations,
  SupplierWithRelations,
  UpdatePurchaseOrder,
  UpdateSupplier,
  UpdateSupplierPaymentTerm,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

interface SupplierListResponse {
  suppliers: SupplierWithRelations[]
  total: number
  page: number
  limit: number
}

interface PurchaseOrderListResponse {
  purchaseOrders: PurchaseOrderWithRelations[]
  total: number
  page: number
  limit: number
}

interface SupplierPerformance {
  supplierId: string
  supplierName: string
  totalOrders: number
  completedOrders: number
  onTimeDeliveryRate: number
  averageLeadTime: number
  qualityScore: number
  totalSpent: number
  averageOrderValue: number
  lastOrderDate: string | null
}

interface SupplierComparison {
  inventoryItemId: string
  itemName: string
  suppliers: Array<{
    supplierId: string
    supplierName: string
    unitCost: number
    leadTimeDays: number
    minimumOrderQty: number
    isPreferred: boolean
    qualityScore: number
    lastOrderDate: string | null
  }>
}

class SupplierApiService extends BaseApi<
  Supplier,
  CreateSupplier,
  UpdateSupplier
> {
  constructor() {
    super({
      baseEndpoint: '/suppliers',
      responseNormalization: true,
    })
  }

  // Supplier CRUD
  async getSuppliers(params?: {
    page?: number
    limit?: number
    search?: string
    isActive?: boolean
    isPreferred?: boolean
    lifecycleStage?: string | string[]
  }) {
    const queryParams = this.buildQueryParams({
      ...(params || {}),
      lifecycleStage: Array.isArray(params?.lifecycleStage)
        ? params.lifecycleStage.join(',')
        : params?.lifecycleStage,
    })
    return this.makeRequest<SupplierListResponse>(
      `${this.config.baseEndpoint}${queryParams}`,
    )
  }

  async getSupplier(id: string) {
    return this.makeRequest<SupplierWithRelations>(
      `${this.config.baseEndpoint}/${id}`,
    )
  }

  async createSupplier(data: CreateSupplier) {
    return this.makeRequest<SupplierWithRelations>(this.config.baseEndpoint, {
      method: 'POST',
      body: data,
    })
  }

  async updateSupplier(id: string, data: UpdateSupplier) {
    return this.makeRequest<SupplierWithRelations>(
      `${this.config.baseEndpoint}/${id}`,
      { method: 'PUT', body: data },
    )
  }

  async deleteSupplier(id: string) {
    return this.makeRequest<{ message: string }>(
      `${this.config.baseEndpoint}/${id}`,
      { method: 'DELETE' },
    )
  }

  async bulkDeleteSuppliers(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(
      `${this.config.baseEndpoint}/bulk-delete`,
      {
        method: 'POST',
        body: { ids },
      },
    )
  }

  // Org-managed payment terms lookup (backs the vendor payment term dropdown)
  async listPaymentTerms() {
    return this.makeRequest<SupplierPaymentTerm[]>(
      `${this.config.baseEndpoint}/payment-terms`,
    )
  }

  async createPaymentTerm(data: CreateSupplierPaymentTerm) {
    return this.makeRequest<SupplierPaymentTerm>(
      `${this.config.baseEndpoint}/payment-terms`,
      { method: 'POST', body: data },
    )
  }

  async updatePaymentTerm(id: string, data: UpdateSupplierPaymentTerm) {
    return this.makeRequest<SupplierPaymentTerm>(
      `${this.config.baseEndpoint}/payment-terms/${id}`,
      { method: 'PUT', body: data },
    )
  }

  async deletePaymentTerm(id: string) {
    return this.makeRequest<{ success: boolean }>(
      `${this.config.baseEndpoint}/payment-terms/${id}`,
      { method: 'DELETE' },
    )
  }

  // Supplier specific endpoints
  async getSupplierPerformance(id: string) {
    return this.makeRequest<SupplierPerformance>(
      `${this.config.baseEndpoint}/${id}/performance`,
    )
  }

  async getSupplierProducts(id: string) {
    return this.makeRequest<SupplierWithRelations>(
      `${this.config.baseEndpoint}/${id}/products`,
    )
  }

  async createSupplierProduct(supplierId: string, data: CreateSupplierProduct) {
    return this.makeRequest<SupplierProductWithRelations>(
      `${this.config.baseEndpoint}/${supplierId}/products`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async getSupplierPurchaseOrders(
    supplierId: string,
    params?: { page?: number; limit?: number; status?: string },
  ) {
    const queryParams = this.buildQueryParams(params || {})
    return this.makeRequest<PurchaseOrderListResponse>(
      `${this.config.baseEndpoint}/${supplierId}/purchase-orders${queryParams}`,
    )
  }

  // Purchase Order CRUD
  async getPurchaseOrders(params?: {
    page?: number
    limit?: number
    status?: string
    supplierId?: string
  }) {
    const queryParams = this.buildQueryParams(params || {})
    return this.makeRequest<PurchaseOrderListResponse>(
      `/purchase-orders${queryParams}`,
    )
  }

  async getPurchaseOrder(id: string) {
    return this.makeRequest<PurchaseOrderWithRelations>(
      `/purchase-orders/${id}`,
    )
  }

  async createPurchaseOrder(data: CreatePurchaseOrder) {
    return this.makeRequest<PurchaseOrderWithRelations>('/purchase-orders', {
      method: 'POST',
      body: data,
    })
  }

  async updatePurchaseOrder(id: string, data: UpdatePurchaseOrder) {
    return this.makeRequest<PurchaseOrderWithRelations>(
      `/purchase-orders/${id}`,
      { method: 'PUT', body: data },
    )
  }

  async updatePurchaseOrderStatus(id: string, status: string) {
    return this.makeRequest<PurchaseOrderWithRelations>(
      `/purchase-orders/${id}/status`,
      { method: 'PUT', body: { status } },
    )
  }

  async receivePurchaseOrderItems(
    id: string,
    items: Array<{ inventoryItemId: string; receivedQty: number }>,
  ) {
    return this.makeRequest<PurchaseOrderWithRelations>(
      `/purchase-orders/${id}/receive`,
      { method: 'POST', body: { items } },
    )
  }

  async deletePurchaseOrder(id: string) {
    return this.makeRequest<{ message: string }>(`/purchase-orders/${id}`, {
      method: 'DELETE',
    })
  }

  // Analysis endpoints
  async compareSuppliers(itemId: string) {
    return this.makeRequest<SupplierComparison>(
      `/supplier-analysis/compare/${itemId}`,
    )
  }

  async getPerformanceDashboard() {
    return this.makeRequest<{ message: string }>(
      '/supplier-analysis/performance-dashboard',
    )
  }

  // Import / Export
  previewImport(
    records: Record<string, unknown>[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(
      `${this.config.baseEndpoint}/import/preview`,
      {
        method: 'POST',
        body: { records, options },
      },
    )
  }

  importSuppliers(data: {
    records: ImportPreviewRecord[]
    options: ImportOptions
  }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(
      `${this.config.baseEndpoint}/import`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  exportSuppliers(options: ExportOptions): Promise<Supplier[]> {
    return this.makeRequest<Supplier[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options,
    })
  }
}

// Export singleton instance
export const supplierApi = createSingletonApi(SupplierApiService)
